// {{WORKTREE_ROOT}}/picosponsorconsole/packages/design/src/components/Button.tsx
// PLAN2 WP09 + PLAN6 §3.7 — primary / secondary / quiet / on-band / danger / dark. All pills (rounded-round).
// `dark` (PLAN6) = bg-band-bg text-band-ink hover:bg-accent, the "Meet the agents" pill. `hero` is sentence case, 18 px bold.
// Canvas refs: Canvas: [0-SYSTEM L125, L157–L158],
// [B-PROOFREVIEW-1440 L1507], [B-SWITCHKIT-1440 L2124], [B-PACKAGE-1440 L1873], [A-HOME-1440 L211–L212],
// [C-ROOM-390 · C-CAPTURE-390 L607], disabled + reason [B-PROMISES-1440 L490–L491]. C-125: `loading` shows three dots
// over a transparent label (width and accessible name unchanged), sets aria-busy + aria-disabled, swallows clicks, keeps
// focus — never the `disabled` attribute. `disabled` is aria-disabled for the same reason (the reason stays announced).
import { forwardRef, useId, type ButtonHTMLAttributes, type ReactNode } from "react";
import { cx } from "../cx.js";
import { DESIGN_TESTIDS as T } from "../testids.js";
import { DesignLink } from "./DesignLink.js";
import { LoadingDots } from "./LoadingDots.js";

export type ButtonVariant = "primary" | "secondary" | "quiet" | "on-band" | "danger" | "dark";
export type ButtonSize = "s" | "m" | "l" | "xl" | "hero" | "capture";

const BASE = "relative inline-flex rounded-round items-center justify-center gap-2 font-sans leading-1.2 no-underline whitespace-nowrap select-none transition-colors";
const TEXT: Record<ButtonSize, string> = {
  s: "text-12 font-bold", m: "text-14 font-bold", l: "text-14 font-bold", xl: "text-15 font-bold",
  hero: "text-18 font-bold", capture: "text-16 font-bold",
};
const MINH: Record<ButtonSize, string> = {
  s: "min-h-target", m: "min-h-target-primary", l: "min-h-target-primary", xl: "min-h-target-primary",
  hero: "min-h-14", capture: "min-h-target-capture w-full",
};
const PAD_FILLED: Record<ButtonSize, string> = { s: "py-2 px-3", m: "py-2.75 px-4", l: "py-3 px-4.5", xl: "py-3.5 px-5.5", hero: "py-4.5 px-7.5", capture: "p-4" };
const PAD_OUTLINED: Record<ButtonSize, string> = { s: "py-1.75 px-2.75", m: "py-2.5 px-3.75", l: "py-2.75 px-4.25", xl: "py-3.25 px-5.25", hero: "py-4.25 px-7.25", capture: "p-3.5" };
const BORDER_OUTLINED: Record<ButtonSize, string> = { s: "border", m: "border-1.5", l: "border-1.5", xl: "border-1.5", hero: "border-2", capture: "border-2" };
const PALETTE: Record<ButtonVariant, string> = {
  primary: "bg-cta text-on-cta hover:bg-cta-hover focus-visible:bg-cta-hover",
  secondary: "bg-surface text-ink border-accent-tint hover:bg-bg-2 focus-visible:bg-bg-2",
  quiet: "bg-surface text-dim border-line hover:text-ink focus-visible:text-ink",
  "on-band": "bg-transparent text-ink border-accent-tint hover:bg-ink hover:text-bg focus-visible:bg-ink focus-visible:text-bg",
  danger: "bg-surface text-red border-red hover:bg-red-wash focus-visible:bg-red-wash",
  dark: "bg-ink text-bg hover:bg-accent hover:text-on-accent focus-visible:bg-accent focus-visible:text-on-accent", // PLAN11: the inverse pill (a black pill would vanish on the black page)
};
const DISABLED = "bg-line text-faint border-line cursor-not-allowed";

export interface ButtonProps extends Omit<ButtonHTMLAttributes<HTMLButtonElement>, "style" | "disabled"> {
  variant?: ButtonVariant;
  size?: ButtonSize;
  /** a mono label look (font-mono uppercase); the only upper case a Button may carry */
  uppercase?: boolean;
  fullWidth?: boolean;
  loading?: boolean;
  /** aria-disabled (never the attribute): stays focusable so the reason is announced */
  disabled?: boolean;
  /** visible mono line under the button, referenced by aria-describedby ("9 unconfirmed · 2 flags") */
  disabledReason?: string;
  iconStart?: ReactNode;
  iconEnd?: ReactNode;
  /** navigates like a link (DesignLink) with the button's look; ignored while loading or disabled */
  href?: string;
  "data-testid"?: string;
}

export const Button = forwardRef<HTMLButtonElement, ButtonProps>(function Button(
  { variant = "primary", size = "m", uppercase: monoLabel = false, fullWidth = false, loading = false, disabled = false, disabledReason,
    iconStart, iconEnd, href, className, children, type = "button", onClick, "aria-describedby": describedBy,
    "data-testid": testId, ...rest },
  ref,
) {
  const reasonId = useId();
  const inert = loading || disabled;
  const outlined = variant !== "primary" && variant !== "dark";
  const classes = cx(
    BASE, TEXT[size], MINH[size],
    outlined ? PAD_OUTLINED[size] : PAD_FILLED[size],
    outlined ? BORDER_OUTLINED[size] : "border-0",
    variant === "quiet" ? "border" : "",
    disabled ? DISABLED : PALETTE[variant],
    monoLabel ? "font-mono uppercase tracking-wide-12" : "",
    fullWidth ? "w-full" : "",
    loading ? "cursor-progress" : inert ? "" : "cursor-pointer",
    className,
  );
  if (href && !inert) {
    return <DesignLink href={href} className={classes} data-testid={testId ?? T.button.root}>{iconStart}{children}{iconEnd}</DesignLink>;
  }
  const described = [describedBy, disabledReason ? reasonId : null].filter(Boolean).join(" ") || undefined;
  const button = (
    <button
      {...rest}
      ref={ref}
      type={type}
      aria-busy={loading || undefined}
      aria-disabled={inert || undefined}
      aria-describedby={described}
      onClick={inert ? (e) => e.preventDefault() : onClick}
      data-testid={testId ?? T.button.root}
      className={classes}
    >
      {loading ? (
        <>
          <span className="inline-flex items-center gap-2 opacity-0">{iconStart}{children}{iconEnd}</span>
          <span className="absolute inset-0 flex items-center justify-center">
            <LoadingDots size={size === "s" ? 5 : 6} data-testid={T.button.dots} />
          </span>
        </>
      ) : (
        <>{iconStart}{children}{iconEnd}</>
      )}
    </button>
  );
  if (!disabledReason) return button;
  return (
    <span className={cx("inline-flex flex-col items-end", fullWidth ? "w-full" : "")}>
      {button}
      <span id={reasonId} data-testid={T.button.reason} className="font-mono text-10 text-faint mt-1">{disabledReason}</span>
    </span>
  );
});
